import React from 'react';
import { TrendingUp, Flame, Users, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { TrendingTopic } from '@/hooks/useArenaTopics';
import { cn } from '@/lib/utils';

interface TrendingTopicsBarProps {
  topics: TrendingTopic[];
  selectedTopics: string[];
  onTopicToggle: (topicId: string) => void;
  onClearFilters: () => void;
  loading?: boolean;
}

export const TrendingTopicsBar: React.FC<TrendingTopicsBarProps> = ({
  topics,
  selectedTopics,
  onTopicToggle,
  onClearFilters,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-2">
        <Flame className="w-4 h-4 text-orange-500 animate-pulse" />
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-8 w-20 rounded-full bg-muted animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (topics.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-orange-500" />
          <span className="text-sm font-medium">Trending Topics</span>
        </div>
        {selectedTopics.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-xs h-7 px-2"
          >
            <X className="w-3 h-3 mr-1" />
            Clear filters
          </Button>
        )}
      </div>

      <ScrollArea className="w-full whitespace-nowrap">
        <div className="flex gap-2 pb-2">
          {topics.map((topic, index) => {
            const isSelected = selectedTopics.includes(topic.interest_id);
            const isHot = index < 3;

            return (
              <button
                key={topic.interest_id}
                onClick={() => onTopicToggle(topic.interest_id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                  'border hover:scale-105 active:scale-95',
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted/50 text-foreground border-border hover:border-primary/50'
                )}
              >
                {isHot && <Flame className="w-3 h-3 text-orange-500" />}
                <span>{topic.interest_name}</span>
                {topic.arena_count > 0 && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'ml-1 h-5 min-w-5 px-1.5 text-xs',
                      isSelected && 'bg-primary-foreground/20 text-primary-foreground'
                    )}
                  >
                    {topic.arena_count}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};

export default TrendingTopicsBar;
